import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Grid,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../api/client';

interface DestinationRecord {
  id: number; name: string; address: string; city: string; state: string;
  zip_code: string; phone: string; notes: string; is_active: boolean;
}

export default function DestinationsPage() {
  const [destinations, setDestinations] = useState<DestinationRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DestinationRecord | null>(null);
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip_code: '', phone: '', notes: '',
  });

  const load = () => {
    api.get('/destinations/all').then((r) => setDestinations(r.data)).catch(() =>
      api.get('/destinations').then((r) => setDestinations(r.data))
    );
  };
  useEffect(() => { load(); }, []);

  const handleOpen = (dest?: DestinationRecord) => {
    if (dest) {
      setEditing(dest);
      setForm({
        name: dest.name, address: dest.address, city: dest.city,
        state: dest.state, zip_code: dest.zip_code, phone: dest.phone, notes: dest.notes,
      });
    } else {
      setEditing(null);
      setForm({ name: '', address: '', city: '', state: '', zip_code: '', phone: '', notes: '' });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/destinations/${editing.id}`, form);
    } else {
      await api.post('/destinations', form);
    }
    setOpen(false);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deactivate this destination?')) return;
    await api.delete(`/destinations/${id}`);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Destinations</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Destination</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Transport destinations (hospitals, facilities, etc.) that dispatchers can select when setting a unit to "Transporting" status.
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={80} />
            </TableRow>
          </TableHead>
          <TableBody>
            {destinations.map((d) => (
              <TableRow key={d.id}>
                <TableCell sx={{ fontWeight: 700 }}>{d.name}</TableCell>
                <TableCell>{d.address}{d.city ? `, ${d.city}` : ''}{d.state ? `, ${d.state}` : ''} {d.zip_code}</TableCell>
                <TableCell>{d.phone || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.notes || '—'}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={d.is_active ? 'Active' : 'Inactive'} color={d.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(d)}><Edit fontSize="small" /></IconButton>
                  {d.is_active && (
                    <IconButton size="small" color="error" onClick={() => handleDelete(d.id)}><Delete fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {destinations.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No destinations yet. Add hospitals, clinics, and other transport facilities.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Destination' : 'New Destination'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mercy Hospital Springfield" />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(417) 555-1234" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="1235 E Cherokee St" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Springfield" />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField fullWidth label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="MO" />
            </Grid>
            <Grid size={{ xs: 12, md: 1.5 }}>
              <TextField fullWidth label="Zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="65804" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={3} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="ER entrance on south side, door code: 4567, trauma center Level II" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
